/**
 * The `HostAdapter` seam (`docs/error-taxonomy.md` § 5) and premise P1 in one interface.
 *
 * **`src/host/` is the ONLY place `@gi-nx/*` may be imported** — enforced twice,
 * by Biome BLOCK 2 (import specifier) and by `.dependency-cruiser.cjs`
 * `only-host-reaches-the-sdk` (graph edge). What lives in THIS file is the
 * interface every other layer programs against, plus the fake that makes the
 * whole call path testable with no Giraffe host on the other end of
 * `postMessage`.
 *
 * ## The real adapter is next door, and the split is load-bearing
 *
 * `realHostAdapter()` lives in `./realAdapter`, which is the file that imports
 * the SDK. It is a separate module because **`@gi-nx/iframe-sdk` touches
 * `window` at MODULE LOAD** (`mini-iframe-rpc.ts:63`) and therefore throws
 * `ReferenceError: window is not defined` the instant it is imported in a bare
 * Node process. One file holding both would mean the fake — whose entire job is
 * to be usable where there is no host — could not be imported by a test.
 *
 * The real implementation **used to live in the app repo that ships the
 * bridge**; it lives here now, and that move is what makes the
 * dependency-cruiser rule mean anything. Until `@gi-nx/iframe-sdk` was an
 * installed dependency of THIS repo, the rule's `to` side matched nothing in
 * the graph and the stage passed by having nothing to say. See
 * `verify:selftest`'s `dep:check/only-host-reaches-the-sdk-is-not-vacuous`.
 *
 * The agent therefore reaches the documented iframe RPC set and nothing else.
 */

export type HostAdapter = {
  /** `rpc.invoke(fn, args)`. Untyped by the SDK; typed by the manifest instead. */
  readonly invoke: (fn: string, args: readonly unknown[]) => Promise<unknown>;
  /**
   * A pushed state key.
   *
   * Returns presence SEPARATELY from the value, because for the geo-typed keys
   * the two are indistinguishable in the value alone: `get()` routes them
   * through `toGeoJSON`, which returns a well-formed EMPTY FeatureCollection
   * for a key that has never arrived (verified 2026-09-09). "Not pushed yet"
   * and "genuinely empty" are different answers and this system reports them
   * differently.
   */
  readonly getState: (key: string) => { readonly present: boolean; readonly value: unknown };
  /**
   * The signed-in user's identity, as an RS256 token Giraffe signed.
   *
   * ⚠️ **Absent from `@gi-nx/iframe-sdk@0.0.15`'s published typings** and
   * reachable only through untyped `rpc.invoke` — on an SDK that has been
   * `0.0.x` for three years. The whole authentication design rests on it, which
   * is ask 3 on issue #19. Gated on APP REGISTRATION, not hosting origin
   * (`requireCallerAppKey`, misc.ts:111 at giraffe-four@50cad4903), so any registered app can obtain one
   * wherever it is served — the reading that licenses this design, and the one
   * that has never been run anywhere but `localhost:5173`.
   *
   * The SDK caches per `(app, project, projects)`, so two calls a second apart return
   * the IDENTICAL token. Do not time it and do not test `exp` freshness with
   * it — that mistake cost the 2026-09-10 probe a corrected finding.
   */
  readonly userToken: () => Promise<string>;
  /**
   * Call `listener` each time the host pushes a NEW value for one of `keys`.
   * Returns the unsubscribe. Over the SDK's public `giraffeState.addListener`
   * (0.0.15 typings).
   *
   * ⚠️ The SDK drops a push whose value is JSON-identical to the one it holds
   * (`iframe-sdk.js`, `GiraffeState.set`), so a write that changes nothing
   * fires nothing. The value is stored BEFORE listeners run, so `getState`
   * inside a listener reads the new value.
   */
  readonly onStateChange: (keys: readonly string[], listener: (key: string) => void) => () => void;
};

/**
 * The host's answer to `getUserClaimsJwt`, turned into a token or a stated
 * failure.
 *
 * Exported so `./realAdapter` shares it rather than restating it. That is the
 * point: if the fake were more forgiving than the real one about a shape the
 * host might actually return, every layer above would be tested against a host
 * kinder than the one it will meet.
 */
export function asHostToken(answer: unknown): string {
  if (typeof answer !== "string" || answer.length === 0) {
    throw new Error(
      `The host answered getUserClaimsJwt with ${typeof answer}, not a token string. ` +
        "The RPC is undocumented in the 0.0.15 typings; if this started happening, the host moved.",
    );
  }
  return answer;
}
